// © 2025 Camp Promethean, LLC. All Rights Reserved.

import { useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { ALL_NAV_ITEMS, PRIMARY_NAV } from "@/config/appNavigation";
import type { NavItem } from "@/types/navigation";
import { useEffectiveAccess } from "@/hooks/useEffectiveAccess";
import { lockReasonFor } from "@/lib/effectiveAccess";

function isNavActive(pathname: string, item: NavItem): boolean {
  const match = item.match ?? "prefix";
  if (match === "exact") return pathname === item.path;
  if (item.path === "/dashboard") return pathname === "/dashboard";
  return pathname === item.path || pathname.startsWith(`${item.path}/`);
}

function findActiveModule(pathname: string): NavItem | undefined {
  return PRIMARY_NAV.find((item) => item.id !== "dashboard" && isNavActive(pathname, item));
}

export function useAppNavigation() {
  const location = useLocation();
  const navigate = useNavigate();
  const { profile, logout } = useAuth();
  const { access } = useEffectiveAccess();

  const activeModule = useMemo(
    () => findActiveModule(location.pathname),
    [location.pathname],
  );

  const breadcrumbs = useMemo(() => {
    const crumbs: { label: string; path?: string }[] = [
      { label: "Dashboard", path: "/dashboard" },
    ];

    if (location.pathname === "/dashboard") return crumbs;

    const navChain = [...ALL_NAV_ITEMS]
      .filter((item) => isNavActive(location.pathname, item))
      .sort((a, b) => a.path.length - b.path.length);

    if (navChain.length > 0) {
      navChain.forEach((item, index) => {
        const isLast = index === navChain.length - 1;
        crumbs.push({
          label: item.breadcrumbLabel ?? item.label,
          path: isLast ? undefined : item.path,
        });
      });
      return crumbs;
    }

    const segments = location.pathname.split("/").filter(Boolean);
    if (segments.length > 0) {
      const label = segments[segments.length - 1]
        .replace(/-/g, " ")
        .replace(/\b\w/g, (c) => c.toUpperCase());
      crumbs.push({ label });
    }

    return crumbs;
  }, [location.pathname]);

  // RB-4: effective access = seat ceiling ∩ facility module policy ∩ role.
  // With no facility context (or resolver unavailable) this degrades to the
  // pre-phase-13 personal-tier check inside lockReasonFor.
  const lockReason = (item: NavItem) =>
    lockReasonFor(access, profile?.membership_tier, item, {
      isPlatformAdmin: profile?.role === "admin",
    });

  const canAccess = (item: NavItem) => lockReason(item) === null;

  const navigateToItem = (item: NavItem) => {
    if (item.id === "logout") {
      logout();
      return;
    }
    const reason = lockReason(item);
    if (reason === "facility") {
      // Scoped out by the facility's blessed admin — not an upsell, no /pricing.
      toast.info("Locked by your facility", {
        description: "Your facility admin has limited access to this module.",
      });
      return;
    }
    if (reason === "tier") {
      toast.info("Upgrade required", {
        description: `This feature requires a ${item.requiredTier} plan.`,
      });
      navigate("/pricing");
      return;
    }
    if (reason === "comingSoon") {
      return;
    }
    if (reason === "admin") {
      toast.info("Admins only", {
        description: "That area is limited to administrators.",
      });
      return;
    }
    navigate(item.path);
  };

  const isItemActive = (item: NavItem) => isNavActive(location.pathname, item);

  const shouldExpandGroup = (item: NavItem) =>
    isItemActive(item) ||
    (item.children?.some((child) => isNavActive(location.pathname, child)) ?? false);

  return {
    activeModule,
    breadcrumbs,
    canAccess,
    lockReason,
    navigateToItem,
    isItemActive,
    shouldExpandGroup,
    pathname: location.pathname,
  };
}
